import { LocaleLink } from "@/components/LocaleLink";
import { useLocale } from "@/lib/i18n";
import { getPostsByTheme, getSortedPosts } from "@/lib/blog-posts";
import { blogThemes } from "@/lib/blog-themes";
import { BlogPostCard } from "@/components/blog/BlogPostCard";
import { localizedHead } from "@/lib/seo";

export const blogIndexHeadDa = () => {
  const loc = localizedHead({ path: "/blog", locale: "da" });
  return {
    meta: [
      { title: "Guides — lamper, håndværk, vintage & indretning | Farveladeland" },
      {
        name: "description",
        content:
          "Guides fra Farveladeland om lamper og belysning, materialer og håndværk, vintage og design samt hjem og indretning — skrevet af os, der står i butikken.",
      },
      { property: "og:title", content: "Guides — Farveladeland" },
      {
        property: "og:description",
        content: "Guides om lamper, håndværk, vintage og indretning.",
      },
      ...loc.meta,
    ],
    links: loc.links,
  };
};

export const blogIndexHeadEn = () => {
  const loc = localizedHead({ path: "/blog", locale: "en" });
  return {
    meta: [
      { title: "Guides — lamps, craft, vintage & interiors | Farveladeland" },
      {
        name: "description",
        content:
          "Guides from Farveladeland on lamps and lighting, materials and craft, vintage and design, and home and interiors — written by the people who run the shop.",
      },
      { property: "og:title", content: "Guides — Farveladeland" },
      {
        property: "og:description",
        content: "Guides on lamps, craft, vintage and interiors.",
      },
      ...loc.meta,
    ],
    links: loc.links,
  };
};

export function BlogIndexPage() {
  const locale = useLocale();
  const latest = getSortedPosts().slice(0, 3);
  const eyebrow = "Guides";
  const titleA = locale === "da" ? "Små" : "Small";
  const titleB = locale === "da" ? "guides." : "guides.";
  const intro =
    locale === "da"
      ? "Vælg et tema og bliv klogere på lamper, materialer, vintage og indretning — skrevet af os, der står i butikken."
      : "Pick a theme and learn more about lamps, materials, vintage and interiors — written by the people who stand in the shop.";
  const seeGuides = locale === "da" ? "Se guides" : "See guides";
  const comingSoon = locale === "da" ? "Guides på vej" : "Guides coming soon";
  const latestTitle = locale === "da" ? "Nyeste guides" : "Latest guides";

  return (
    <>
      <section className="pt-40 pb-12 md:pt-52 md:pb-16">
        <div className="mx-auto max-w-[1500px] px-6 md:px-10">
          <p className="eyebrow">{eyebrow}</p>
          <h1 className="mt-6 max-w-4xl font-display text-[clamp(3rem,7vw,6rem)] leading-[0.95] text-balance">
            {titleA} <em className="text-terracotta">{titleB}</em>
          </h1>
          <p className="mt-6 max-w-xl text-base leading-relaxed text-foreground/75">
            {intro}
          </p>
        </div>
      </section>

      <section className="mx-auto max-w-[1500px] px-6 pb-24 md:px-10 md:pb-32">
        <div className="grid gap-x-6 gap-y-14 md:grid-cols-2 lg:gap-x-8 lg:gap-y-20">
          {blogThemes.map((theme) => {
            const c = theme[locale];
            const count = getPostsByTheme(theme.id).length;
            return (
              <LocaleLink
                key={theme.id}
                to="/blog/$slug"
                params={{ slug: theme.id }}
                className="group block"
              >
                <div className="aspect-[16/10] overflow-hidden rounded-sm bg-muted">
                  <img
                    src={theme.image}
                    alt={theme.imageAlt[locale]}
                    loading="lazy"
                    decoding="async"
                    className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.04]"
                  />
                </div>
                <div className="mt-6 max-w-xl">
                  <p className="text-[10px] uppercase tracking-[0.22em] text-terracotta">
                    {c.label}
                  </p>
                  <h2 className="mt-3 font-display text-3xl leading-tight text-foreground transition-colors group-hover:text-terracotta md:text-4xl">
                    {c.title}
                  </h2>
                  <p className="mt-3 text-sm leading-relaxed text-foreground/70 md:text-base">
                    {c.teaser}
                  </p>
                  <p className="mt-5 inline-flex items-center gap-2 text-xs font-medium uppercase tracking-[0.22em] text-foreground">
                    {count > 0 ? `${seeGuides} (${count})` : comingSoon}
                    <span
                      aria-hidden
                      className="transition-transform duration-300 group-hover:translate-x-1"
                    >
                      →
                    </span>
                  </p>
                </div>
              </LocaleLink>
            );
          })}
        </div>
      </section>

      <section className="border-t border-border/60">
        <div className="mx-auto max-w-[1500px] px-6 py-20 md:px-10 md:py-28">
          <h2 className="font-display text-3xl text-foreground md:text-4xl">{latestTitle}</h2>
          <div className="mt-10 grid gap-12 md:grid-cols-2 lg:grid-cols-3 lg:gap-x-10">
            {latest.map((post) => (
              <BlogPostCard key={post.slug} post={post} />
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
